import { useTranslation } from 'react-i18next'
import { HuntReplayViewer } from '@wehunt/ui'
import type { ReplayBundle } from '@wehunt/ui'
import { ReplayStatusScreen } from './ReplayStatusScreen'
import { isReplayStillGenerating } from './replayReadiness'

export interface ReplayBundleViewProps {
  bundle: ReplayBundle
  onClose: () => void
  /**
   * Omitted for a snapshot that cannot change (a share), where asking again
   * could only return the same still-building archive.
   */
  onRetry?: () => void
}

/**
 * What a loaded replay turns into: the viewer, or the status screen for an
 * archive that has nothing to animate yet.
 *
 * Shared by the hunt route and the share route so the two cannot disagree about
 * when a replay is "still building" and when it is simply empty.
 */
export function ReplayBundleView({ bundle, onClose, onRetry }: ReplayBundleViewProps) {
  const { t } = useTranslation('replay')

  // An archive with no positions at all parses fine but has nothing to animate;
  // an empty map with working controls would read as a bug.
  const hasAnyFix = bundle.participants.some((p) => p.fixes.length > 0)

  // A just-ended hunt whose archive has not finished building parses fine and
  // carries participants with no fixes. Reading that as "nothing was recorded"
  // is not merely unhelpful, it is false — the positions exist and arrive
  // minutes later. Retry is offered because, unlike the empty case, this one
  // really does resolve on its own.
  if (
    isReplayStillGenerating({
      endsAt: bundle.endsAt,
      participantCount: bundle.participants.length,
      hasAnyFix,
    })
  ) {
    return (
      <ReplayStatusScreen
        title={t('status.generatingTitle')}
        body={t('status.generatingBody')}
        onClose={onClose}
        onRetry={onRetry}
      />
    )
  }

  if (!hasAnyFix) {
    return (
      <ReplayStatusScreen
        title={t('status.emptyTitle')}
        body={t('status.emptyBody')}
        onClose={onClose}
      />
    )
  }

  return <HuntReplayViewer bundle={bundle} onClose={onClose} />
}
